import { DM_DELIVERY_ADDRESS, DM_DELIVERY_CLICK_AND_COLLECT, DM_DELIVERY_COOLOMAT, DM_DELIVERY_COOLOMAT_LOCKER, DM_DELIVERY_FOXPOST_LOCKER, DM_DELIVERY_FOXPOST_PICKUP, DM_DELIVERY_GLS_LOCKER, DM_DELIVERY_GLS_PICKUP, DM_DELIVERY_LOCKER, DM_DELIVERY_PICKUP, DM_DELIVERY_PICKUP_OR_LOCKER } from '~~/common/config/DeliveryMethod';
import type { Schema } from '~~/common/types/api';

// Use generated types from OpenAPI spec
type IDeliveryMethod = Schema<'DeliveryMethodModel'>;
type NonFoodCourier = Schema<'NonFoodCourier'>;
type PriceInterface = Schema<'PriceInterface'>;
type ExtendedPrice = Schema<'ExtendedPrice'>;
type PaymentInfo = { name: string; value: boolean };

// Internal config types
type AreaModel = 'public_area' | 'department_store' | null;

interface DeliveryMethodBase {
  areaModel: AreaModel;
  token: string;
}

export interface ChildDeliveryMethod {
  areaModel: AreaModel;
  token: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  disabledReason?: string | null;
  lockerInfoMessage?: string | null;
  nonFoodCourier?: NonFoodCourier | null;
  promotional?: boolean;
  additionalText?: string | null;
}

interface ChildrenGroup {
  token: string;
  available: string[];
  children: Record<string, ChildDeliveryMethod>;
}

interface MinPriceAccumulator {
  grossDiscounted: number;
}

/**
 * The synthetic "pickup point or locker" entry this file assembles from the individual courier
 * methods. `deliveryMethodMapper` runs over both these and the API's own `DeliveryMethodModel`s, so
 * the fields only the latter carries are declared here as optional — the mapper reads them
 * defensively either way.
 */
interface PickupOrLockerDeliveryMethod {
  id: string;
  areaModel: null;
  token: string;
  childrenGroups: Record<string, ChildrenGroup>;
  price?: PriceInterface | MinPriceAccumulator | null;
  promotionalPrice?: PriceInterface | MinPriceAccumulator | null;
  paymentInfos?: PaymentInfo[];
  promotional?: boolean;
  name?: string;
  disabled?: boolean;
  disabledReason?: string | null;
  nonFoodCourier?: NonFoodCourier | null;
}

/** Whatever `deliveryMethodMapper` accepts: an API delivery method or the synthetic entry above. */
type MappableDeliveryMethod = IDeliveryMethod | PickupOrLockerDeliveryMethod;

export interface MappedDeliveryMethod extends DeliveryMethodBase {
  value: string;
  id?: string;
  name?: string;
  nonFoodCourier?: NonFoodCourier | null;
  isDisabled?: boolean;
  disabledReason?: string | null;
  childrenGroups?: Record<string, ChildrenGroup>;
  price?: PriceInterface | MinPriceAccumulator | null;
  promotional?: boolean;
  promotionalPrice?: ExtendedPrice | PriceInterface | MinPriceAccumulator | null;
  paymentInfos?: PaymentInfo[];
  additionalText?: string | null;
}

interface CalculatePickupOrLockerMethodsResult {
  pickupOrLockerMethods: PickupOrLockerDeliveryMethod[];
  obj: PickupOrLockerDeliveryMethod;
}

type AllDeliveryMethods = Record<string, DeliveryMethodBase | PickupOrLockerDeliveryMethod>;

export const DELIVERY_AREA_MODEL = {
  PUBLIC_AREA: 'public_area',
  PICK_UP_FROM_STORE: 'department_store',
} as const;

const allDeliveryMethods: AllDeliveryMethods = {
  [DM_DELIVERY_ADDRESS]: {
    areaModel: DELIVERY_AREA_MODEL.PUBLIC_AREA,
    token: 'aw.pages.checkout.delivery.mode.home_delivery',
  },
  [DM_DELIVERY_CLICK_AND_COLLECT]: {
    areaModel: DELIVERY_AREA_MODEL.PICK_UP_FROM_STORE,
    token: 'aw.pages.checkout.delivery.mode.click_and_collect',
  },
  [DM_DELIVERY_PICKUP_OR_LOCKER]: {
    id: DM_DELIVERY_PICKUP_OR_LOCKER,
    areaModel: null,
    token: 'aw.pages.checkout.delivery.mode.pickup_locker',
    childrenGroups: {
      [DM_DELIVERY_COOLOMAT]: {
        token: 'aw.pages.checkout.delivery.mode.coolomat_tab_label',
        available: [],
        children: {
          [DM_DELIVERY_COOLOMAT_LOCKER]: {
            areaModel: null,
            token: 'aw.pages.checkout.delivery.mode.coolomat',
          },
        },
      },
      [DM_DELIVERY_PICKUP]: {
        token: 'aw.pages.checkout.delivery.mode.pickup',
        available: [],
        children: {
          [DM_DELIVERY_GLS_PICKUP]: {
            areaModel: null,
            token: 'aw.pages.checkout.delivery.mode.gls',
          },
          [DM_DELIVERY_FOXPOST_PICKUP]: {
            areaModel: null,
            token: 'aw.pages.checkout.delivery.mode.foxpost',
          },
        },
      },
      [DM_DELIVERY_LOCKER]: {
        token: 'aw.pages.checkout.delivery.mode.locker',
        available: [],
        children: {
          [DM_DELIVERY_GLS_LOCKER]: {
            areaModel: null,
            token: 'aw.pages.checkout.delivery.mode.gls',
          },
          [DM_DELIVERY_FOXPOST_LOCKER]: {
            areaModel: null,
            // ilyen token nincsen aw.timeframe.modal.dm.foxpost
            token: 'aw.pages.checkout.delivery.mode.foxpost',
          },
        },
      },
    },
  },
};

function getPairings (checkoutAvailables: IDeliveryMethod[]) {
  const dmToLockerOrPickup = {
    [DM_DELIVERY_GLS_PICKUP]: DM_DELIVERY_PICKUP,
    [DM_DELIVERY_GLS_LOCKER]: DM_DELIVERY_LOCKER,
    [DM_DELIVERY_FOXPOST_PICKUP]: DM_DELIVERY_PICKUP,
    [DM_DELIVERY_FOXPOST_LOCKER]: DM_DELIVERY_LOCKER,
    [DM_DELIVERY_COOLOMAT_LOCKER]: DM_DELIVERY_COOLOMAT,
  };
  type PairingResults = {
    [K in keyof typeof dmToLockerOrPickup]?: {
      type: string;
      list: IDeliveryMethod | null;
    };
  };
  const pairingResults: PairingResults = {};
  for (const dm in dmToLockerOrPickup) {
    const validKey = (dm as keyof typeof dmToLockerOrPickup);
    const type = dmToLockerOrPickup[validKey];
    if (type) {
      pairingResults[validKey] = {
        type,
        list: checkoutAvailables.find(({ id }) => id === dm) ?? null,
      };
    }
  }
  return pairingResults;
}


export const calculatePickupOrLockerMethods = (
  checkoutAvailables: IDeliveryMethod[],
): CalculatePickupOrLockerMethodsResult => {
  const pickupOrLockerDeliveryMethod = allDeliveryMethods[DM_DELIVERY_PICKUP_OR_LOCKER] as PickupOrLockerDeliveryMethod;
  let obj: PickupOrLockerDeliveryMethod = JSON.parse(JSON.stringify(pickupOrLockerDeliveryMethod));

  const localPairings = getPairings(checkoutAvailables);
  const pickupOrLockerMethods: PickupOrLockerDeliveryMethod[] = [];

  if (Object.values(localPairings).some(({ list }) => list)) {
    Object.entries(localPairings).forEach(([key, {
      type,
      list,
    }]) => {
      if (list) {
        const childrenGroup = obj.childrenGroups[type];
        if (childrenGroup) {
          const existingChild = childrenGroup.children[key];
          if (existingChild) {
            childrenGroup.children[key] = {
              ...existingChild,
              ...list,
              areaModel: existingChild.areaModel,
              token: existingChild.token,
            };
            childrenGroup.available.push(key);
          }
        }
      }
    });

    /**
     * If any of nonFoodCourier prices are missing then we can not display
     * the lowest price.
     */
    let isPriceKnown = true;


    const calculatedMinPrice = (Object.entries(localPairings)
      .map(([, { list }]) => list)
      .filter((list) => list !== null)
      .reduce((minPrice: MinPriceAccumulator, currentDeliveryMethodObj): MinPriceAccumulator => {
        const price = currentDeliveryMethodObj.nonFoodCourier?.price;
        if (isPriceKnown) {
          isPriceKnown = (typeof price?.grossDiscounted === 'number');
        }
        if (!isPriceKnown) {
          return { grossDiscounted: Infinity };
        } else {
          return (price && (price?.grossDiscounted < minPrice.grossDiscounted)
            ? price
            : minPrice
          );
        }
      }, { grossDiscounted: Infinity })
    );

    const availableLists = Object.values(localPairings)
      .map(({ list }) => list)
      .filter((list): list is IDeliveryMethod => list !== null);

    const firstPaymentInfos = availableLists.find((list) => list.paymentInfos?.length)?.paymentInfos ?? [];
    const isPromotional = availableLists.some((list) => list.promotional || list.nonFoodCourier?.promotional);

    let isPromotionalPriceKnown = true;
    const calculatedMinPromotionalPrice = availableLists
      .filter((list) => list.promotional || list.nonFoodCourier?.promotional)
      .reduce((minPrice: MinPriceAccumulator, dm): MinPriceAccumulator => {
        const price = dm.nonFoodCourier?.price;
        if (isPromotionalPriceKnown) {
          isPromotionalPriceKnown = (typeof price?.grossDiscounted === 'number');
        }
        if (!isPromotionalPriceKnown) {
          return { grossDiscounted: Infinity };
        }
        return (price && price.grossDiscounted < minPrice.grossDiscounted ? price : minPrice);
      }, { grossDiscounted: Infinity });

    obj = {
      ...obj,
      price: (calculatedMinPrice.grossDiscounted === Infinity
        ? null
        : calculatedMinPrice
      ),
      promotionalPrice: (isPromotional && isPromotionalPriceKnown && calculatedMinPromotionalPrice.grossDiscounted !== Infinity
        ? calculatedMinPromotionalPrice
        : null
      ),
      paymentInfos: firstPaymentInfos,
      promotional: isPromotional,
    };

    pickupOrLockerMethods.push(obj);
  }
  return { pickupOrLockerMethods, obj };
};

export const deliveryMethodMapper = (obj: PickupOrLockerDeliveryMethod) => (dm: MappableDeliveryMethod): MappedDeliveryMethod => {
  const original = dm.id === DM_DELIVERY_PICKUP_OR_LOCKER ? obj : (dm.id ? allDeliveryMethods[dm.id] : undefined);
  let isDisabled = dm.disabled ?? false;
  let disabledReason = dm.disabledReason ?? null;

  if (original && 'childrenGroups' in original && original.childrenGroups) {
    interface DisabledState {
      isDisabled: boolean | null;
      disabledReason: string | null;
    }

    const result = (Object.values(original.childrenGroups)
      .reduce(
        (acc: DisabledState, childrenGroup): DisabledState => {
          const childrenArray = Object.values(childrenGroup.children) as ChildDeliveryMethod[];
          const availableChildren = childrenArray.filter((child) => {
            return !!child.id && childrenGroup.available.includes(child.id);
          });


          return availableChildren.reduce(
            (acc1: DisabledState, child): DisabledState => {
              acc1.isDisabled = !!((acc1.isDisabled === null || acc1.isDisabled === true) && child.disabled);
              if (!acc1.disabledReason) {
                acc1.disabledReason = child.disabledReason ?? null;
              }
              return acc1;
            },
            acc,
          );
        },
        {
          isDisabled: null as boolean | null,
          disabledReason: null as string | null,
        },
      )
    );
    isDisabled = result.isDisabled || false;
    disabledReason = result.disabledReason || '';
  }

  if (!original) {
    throw new Error(`Unknown delivery method: ${dm.id}`);
  }

  if ('childrenGroups' in original && original.childrenGroups) {
    const pickupOrLockerOriginal = original as PickupOrLockerDeliveryMethod;
    const result: MappedDeliveryMethod = {
      areaModel: pickupOrLockerOriginal.areaModel,
      token: pickupOrLockerOriginal.token,
      value: dm.nonFoodCourier ? `${dm.id}_${dm.nonFoodCourier.id}` : (dm.id ?? ''),
      id: dm.id,
      name: dm.name,
      nonFoodCourier: dm.nonFoodCourier,
      isDisabled,
      disabledReason,
      childrenGroups: pickupOrLockerOriginal.childrenGroups,
      price: pickupOrLockerOriginal.price,
      promotional: dm.promotional ?? dm.nonFoodCourier?.promotional ?? false,
      promotionalPrice: pickupOrLockerOriginal.promotionalPrice ?? null,
      paymentInfos: dm.paymentInfos ?? [],
    };
    return result;
  }

  const result: MappedDeliveryMethod = {
    areaModel: 'areaModel' in original ? original.areaModel : null,
    token: 'token' in original ? original.token : '',
    value: dm.nonFoodCourier ? `${dm.id}_${dm.nonFoodCourier.id}` : (dm.id ?? ''),
    id: dm.id,
    name: dm.name,
    nonFoodCourier: dm.nonFoodCourier,
    isDisabled,
    disabledReason,
    promotional: dm.promotional ?? dm.nonFoodCourier?.promotional ?? false,
    promotionalPrice: dm.promotionalPrice ?? null,
    paymentInfos: dm.paymentInfos ?? [],
    additionalText: dm.nonFoodCourier?.additionalText ?? null,
  };

  return result;
};
